import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { NavItem } from 'core/models/nav-item.model';
import { isNavItemActive } from 'core/utils/nav-item-active.util';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, NgClass],
  templateUrl: './sidebar.html',
})
export class Sidebar {
  private readonly router = inject(Router);

  readonly isOpen = input(true);
  readonly closeSidebar = output<void>();
  readonly navItems = input<readonly NavItem[]>([]);
  readonly topOffset = input('0px');
  readonly brandLabel = input('Admin');

  private readonly isDesktop = signal(false);
  private readonly expandedGroupLabel = signal<string | null>(null);
  private readonly currentUrl = signal(this.router.url);

  constructor() {
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function'
    ) {
      const media = window.matchMedia('(min-width: 1024px)');
      this.isDesktop.set(media.matches);
      media.addEventListener('change', (event) =>
        this.isDesktop.set(event.matches),
      );
    }

    effect(() => {
      const items = this.navItems();
      untracked(() => this.expandActiveGroups(items));
    });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
        this.expandActiveGroups(this.navItems());
      });
  }

  protected onNavClick(): void {
    if (!this.isDesktop()) {
      this.closeSidebar.emit();
    }
  }

  protected hasChildren(item: NavItem): boolean {
    return (item.children?.length ?? 0) > 0;
  }

  protected isGroupExpanded(label: string): boolean {
    return this.expandedGroupLabel() === label;
  }

  protected toggleGroup(item: NavItem): void {
    this.expandedGroupLabel.update((current) =>
      current === item.label ? null : item.label,
    );
  }

  protected isItemActive(item: NavItem): boolean {
    return isNavItemActive(item, this.currentUrl());
  }

  protected isDescendantActive(item: NavItem): boolean {
    return isNavItemActive(item, this.currentUrl());
  }

  protected readonly ariaHidden = computed(() =>
    this.isOpen() || this.isDesktop() ? null : 'true',
  );

  private expandActiveGroups(items: readonly NavItem[]): void {
    const active = items.find(
      (item) => this.hasChildren(item) && this.isDescendantActive(item),
    );
    if (active) {
      this.expandedGroupLabel.set(active.label);
    }
  }
}
